import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Database, Download, MoreHorizontal, Plus, Search, X } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { cn } from '../../lib/cn';
import { useAdminBusinesses, useResellers } from '../../api/admin';
import { apiPost } from '../../api/client';

// Record<string, …> + fallback: covers every business_status the API can emit
// (the DB enum has 7 values; the happy-path mock only used 3) without a gray badge.
const STATUS_TONE: Record<string, 'success' | 'info' | 'warning' | 'error' | 'neutral'> = {
  active: 'success',
  trial: 'info',
  past_due: 'warning',
  suspended: 'error',
  canceled: 'neutral',
  pending_payment: 'warning',
  provisioning: 'info',
  deleted: 'neutral',
};
const STATUS_TONE_FALLBACK = 'neutral' as const;

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trial: 'Trial',
  past_due: 'Past due',
  suspended: 'Suspended',
  canceled: 'Canceled',
  pending_payment: 'Pending pay',
  provisioning: 'Provisioning',
  deleted: 'Deleted',
};

const DB_LABEL = {
  managed: 'Managed',
  self_hosted: 'Self-host',
  legacy: 'Legacy',
};

/** All businesses across all resellers — multi-filter, bulk actions footer. */
export function Businesses() {
  const { data: adminBusinesses = [], isLoading } = useAdminBusinesses();
  const { data: resellers = [] } = useResellers();
  const [resellerFilter, setResellerFilter] = useState<string>('all');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dbFilter, setDbFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    return adminBusinesses.filter((b) => {
      if (resellerFilter !== 'all') {
        if (resellerFilter === 'direct' ? b.resellerId !== null : b.resellerId !== resellerFilter) return false;
      }
      if (planFilter !== 'all' && b.plan !== planFilter) return false;
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      if (dbFilter !== 'all' && b.dbMode !== dbFilter) return false;
      if (search && !b.name.toLowerCase().includes(search.toLowerCase()) && !b.ownerEmail.includes(search.toLowerCase()) && !b.id.includes(search)) return false;
      return true;
    });
  }, [resellerFilter, planFilter, statusFilter, dbFilter, search, adminBusinesses]);

  function toggleAll() {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((b) => b.id)));
  }

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto pb-32">
      <PageHeader
        eyebrow="Platform"
        title={<span>Businesses {isLoading ? <Skeleton className="inline-block w-12 h-5 rounded align-middle" /> : <span className="text-zinc-400 dark:text-zinc-500 font-medium tabular-nums">({adminBusinesses.length})</span>}</span>}
        subtitle="Every Possignin business across every reseller and direct sale."
        action={
          <>
            <Button variant="secondary" size="sm" iconLeft={<Download className="w-4 h-4" />}>
              <span className="hidden sm:inline">Export CSV</span>
            </Button>
            <Link to="/businesses/new">
              <Button size="sm" iconLeft={<Plus className="w-4 h-4" />}>New business</Button>
            </Link>
          </>
        }
      />

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <FilterChip
            label="Reseller"
            value={
              resellerFilter === 'all'
                ? 'All'
                : resellerFilter === 'direct'
                ? 'Direct'
                : resellers.find((r) => r.id === resellerFilter)?.name ?? 'All'
            }
            options={[
              { v: 'all', l: 'All' },
              { v: 'direct', l: 'Direct (no reseller)' },
              ...resellers.map((r) => ({ v: r.id, l: r.name })),
            ]}
            onChange={setResellerFilter}
          />
          <FilterChip
            label="Plan"
            value={planFilter === 'all' ? 'All' : planFilter}
            options={[
              { v: 'all', l: 'All' },
              { v: 'Starter', l: 'Starter' },
              { v: 'Basic', l: 'Basic' },
              { v: 'Pro', l: 'Pro' },
              { v: 'Enterprise', l: 'Enterprise' },
            ]}
            onChange={setPlanFilter}
          />
          <FilterChip
            label="Status"
            value={statusFilter === 'all' ? 'All' : STATUS_LABEL[statusFilter] ?? statusFilter}
            options={[
              { v: 'all', l: 'All' },
              { v: 'active', l: 'Active' },
              { v: 'trial', l: 'Trial' },
              { v: 'past_due', l: 'Past due' },
              { v: 'suspended', l: 'Suspended' },
              { v: 'canceled', l: 'Canceled' },
            ]}
            onChange={setStatusFilter}
          />
          <FilterChip
            label="DB"
            value={dbFilter === 'all' ? 'All' : DB_LABEL[dbFilter as keyof typeof DB_LABEL]}
            options={[
              { v: 'all', l: 'All' },
              { v: 'managed', l: 'Managed' },
              { v: 'self_hosted', l: 'Self-hosted' },
              { v: 'legacy', l: 'Legacy (shared)' },
            ]}
            onChange={setDbFilter}
          />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search by name, owner, domain, business_id…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="px-5 sm:px-6 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleAll}
                    className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                </th>
                <th className="text-left font-medium px-4 py-3">Name</th>
                <th className="text-left font-medium px-4 py-3">Reseller</th>
                <th className="text-left font-medium px-4 py-3">Plan</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">DB</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3"><Skeleton className="w-4 h-4 rounded" /></td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-40 max-w-full rounded mb-1.5" />
                      <Skeleton className="h-3 w-28 max-w-full rounded" />
                    </td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-20 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-14 rounded" /></td>
                    <td className="px-5 sm:px-6 py-3"></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No businesses match these filters.
                  </td>
                </tr>
              ) : (
                filtered.map((b) => (
                <tr key={b.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(b.id)}
                      onChange={() => toggle(b.id)}
                      className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to="/admin/businesses/$id"
                      params={{ id: b.id }}
                      className="block min-w-0 group"
                    >
                      <div className="font-medium text-zinc-950 dark:text-zinc-50 group-hover:text-brand dark:group-hover:text-brand-dm truncate transition">
                        {b.name}
                      </div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{b.ownerEmail}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 whitespace-nowrap">
                    {b.resellerName ?? <span className="text-zinc-400 dark:text-zinc-500">— Direct</span>}
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{b.plan}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Badge tone={STATUS_TONE[b.status] ?? STATUS_TONE_FALLBACK}>{STATUS_LABEL[b.status] ?? b.status}</Badge>
                      {b.paymentReviewAt && <ReviewFlag id={b.id} />}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 hidden md:table-cell">{DB_LABEL[b.dbMode]}</td>
                  <td className="px-5 sm:px-6 py-3 text-right">
                    <ProvisionAction id={b.id} dbMode={b.dbMode} />
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500 dark:text-zinc-400 flex items-center justify-between">
          {isLoading ? <Skeleton className="h-3 w-28 rounded" /> : <span>Showing <span className="tabular-nums">{filtered.length}</span> of <span className="tabular-nums">{adminBusinesses.length}</span></span>}
          <span className="hidden sm:inline">Click any row to drill in. Use bulk bar below for multi-business actions.</span>
        </div>
      </Card>

      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 max-w-2xl w-[calc(100%-2rem)] rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-lg p-3 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100 px-2">
            <span className="tabular-nums">{selected.size}</span> selected
          </span>
          <span className="h-5 w-px bg-zinc-200 dark:bg-zinc-700" />
          <Button variant="secondary" size="sm">Suspend</Button>
          <Button variant="secondary" size="sm">Migrate</Button>
          <Button variant="secondary" size="sm" iconLeft={<Download className="w-3.5 h-3.5" />}>Export</Button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="ml-auto w-7 h-7 grid place-items-center rounded text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
            aria-label="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/** The payment-review badge + a clear affordance (admin resolved the chargeback). */
function ReviewFlag({ id }: { id: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  async function clear() {
    setBusy(true);
    try {
      await apiPost(`/admin/businesses/${id}/clear-review`);
      void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
    } catch {
      /* leave the flag set on error */
    } finally {
      setBusy(false);
    }
  }
  return (
    <span className="inline-flex items-center gap-0.5">
      <Badge tone="warning">Review</Badge>
      <button
        type="button"
        onClick={clear}
        disabled={busy}
        title="Clear payment review (resolved)"
        className="w-4 h-4 grid place-items-center rounded text-amber-600 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-900/40 transition disabled:opacity-50"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

/** Row action: trigger dedicated-DB provisioning for a managed business (idempotent). */
function ProvisionAction({ id, dbMode }: { id: string; dbMode: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  if (dbMode !== 'managed') {
    return (
      <button type="button" disabled className="w-8 h-8 grid place-items-center rounded-md text-zinc-300 dark:text-zinc-600 cursor-default">
        <MoreHorizontal className="w-4 h-4" />
      </button>
    );
  }

  async function provision() {
    setBusy(true);
    setResult(null);
    try {
      const r = await apiPost<{ status: string }>(`/admin/businesses/${id}/provision`);
      setResult(r.status);
      void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
    } catch {
      setResult('error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={provision}
      disabled={busy}
      title="Provision dedicated database"
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition',
        result === 'ready'
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800'
          : result === 'error'
            ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/60 dark:text-red-300 dark:border-red-800'
            : 'bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800',
      )}
    >
      <Database className="w-3.5 h-3.5" />
      {busy ? 'Provisioning…' : result === 'ready' ? 'Ready' : result === 'error' ? 'Failed' : result ?? 'Provision'}
    </button>
  );
}

function FilterChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ v: string; l: string }>;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
      >
        <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-20 min-w-[180px] rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-md py-1 max-h-64 overflow-y-auto">
            {options.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className={cn(
                  'block w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800',
                  value === o.l ? 'text-brand dark:text-brand-dm font-medium' : 'text-zinc-700 dark:text-zinc-200',
                )}
              >
                {o.l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
